// HowToPlayPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import Seo, { breadcrumbLd } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import { useLang } from '../i18n';

const C = {
  tr: {
    title: 'Şogi Nasıl Oynanır?',
    seoDesc: 'Japon satrancı şogi nasıl oynanır? Oyunun amacı, sente ve gote, hamle yapma, elden taş sürme ve kazanma koşulları adım adım Türkçe anlatım.',
    lead: 'Şogiyi beş dakikada kavra: amaç, sıra ve bir hamlenin adımları.',
    goalTitle: 'Amaç ve sıra',
    goal: [
      'Şogi (Japon satrancı) iki kişilik bir strateji oyunudur. Amaç, rakibin şahını (王/玉) kaçacak yeri kalmayacak şekilde tehdit altına almaktır; buna mat denir.',
      'Oyuncular sırayla bir hamle yapar: ya tahtadaki bir taşını oynatır ya da daha önce ele geçirdiği bir taşı elinden boş bir kareye sürer (drop).',
      'Sente (先手, ilk oynayan) tahtanın altında, Gote (後手, ikinci oynayan) üstünde oturur. Taşlar iki tarafta da aynı renktedir; kime ait olduğu sivri ucunun yönünden anlaşılır.',
    ],
    stepsTitle: 'Adım adım bir hamle',
    steps: [
      'Sıra sendeyse tahtadaki bir taşına dokun; gidebileceği kareler yeşil noktalarla işaretlenir.',
      'İşaretli bir kareye dokunarak taşı oynat. Rakip taş varsa onu alırsın ve taş eline geçer.',
      'Taşın rakibin son üç sırasına (terfi bölgesi) girer, orada hareket eder veya oradan çıkarsa terfi edip etmeyeceğin sorulur.',
      'Elinde taş varsa ona dokunup uygun boş bir kareye sürebilirsin. Sürülen taş terfisiz yüzüyle konur.',
      'Rakibin şahını kaçışsız bıraktığında kazanırsın. Kendi şahını şaha bırakan hamleler yapılamaz.',
    ],
    winTitle: 'Oyun nasıl biter?',
    win: [
      'Mat: Şahı tehdit altındaki oyuncunun bu tehdidi kaldıracak yasal hamlesi yoksa oyunu kaybeder.',
      'Şogide pat yoktur: yasal hamlesi kalmayan oyuncu kaybeder.',
      'Teslim olmak resmi oyunlarda yaygındır; oyuncu "makemashita" (kaybettim) diyerek oyunu bitirir.',
    ],
    nextTitle: 'Sırada ne var?',
    next: (to) => (
      <>
        Taşların hareketleri için <Link to={to('pieces')}>Şogi Taşları</Link>, terfi ve elden taş sürme gibi
        ayrıntılar için <Link to={to('rules')}>Kurallar</Link> sayfasına göz at. Hazır olduğunda{' '}
        <Link to={to('play')}>bilgisayara karşı Kolay seviyede</Link> başla ya da{' '}
        <Link to={to('puzzles')}>1 hamlede mat bulmacalarıyla</Link> ısın.
      </>
    ),
  },
  en: {
    title: 'How to Play Shogi',
    seoDesc: 'How to play shogi (Japanese chess): the goal, sente and gote, making a move, dropping captured pieces and how the game ends — explained step by step.',
    lead: 'Understand shogi in five minutes: the goal, the turn order and what a move looks like.',
    goalTitle: 'Goal and turn order',
    goal: [
      "Shogi (Japanese chess) is a two-player strategy game. The goal is to attack the opponent's king (王/玉) so that it has no escape — checkmate.",
      'Players alternate turns. On each turn you either move one of your pieces on the board or drop a piece you captured earlier onto an empty square.',
      'Sente (先手, first player) sits at the bottom and Gote (後手, second player) at the top. All pieces are the same colour; ownership is shown by the direction the wedge-shaped piece points.',
    ],
    stepsTitle: 'A move, step by step',
    steps: [
      'When it is your turn, tap one of your pieces; the squares it can reach are marked with green dots.',
      "Tap a marked square to move there. If an enemy piece is there you capture it and it goes to your hand.",
      "When a piece enters, moves within or leaves the opponent's last three ranks (the promotion zone), you are asked whether to promote it.",
      'If you hold pieces in hand, tap one and then a legal empty square to drop it. Dropped pieces always land unpromoted.',
      "You win when the opponent's king cannot escape. Moves that leave your own king in check are not allowed.",
    ],
    winTitle: 'How does the game end?',
    win: [
      'Checkmate: if the player in check has no legal move that removes the threat, they lose.',
      'There is no stalemate in shogi: a player with no legal moves loses.',
      'Resigning is common in serious games; a player says "makemashita" (I have lost).',
    ],
    nextTitle: "What's next?",
    next: (to) => (
      <>
        See how each piece moves on <Link to={to('pieces')}>Shogi Pieces</Link>, and promotion, drops and
        other details on <Link to={to('rules')}>Rules</Link>. When you are ready, start{' '}
        <Link to={to('play')}>against the computer on Easy</Link> or warm up with{' '}
        <Link to={to('puzzles')}>mate-in-one puzzles</Link>.
      </>
    ),
  },
};

export default function HowToPlayPage() {
  const { lang, to, t } = useLang();
  const c = C[lang];

  const howToLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: c.title,
    description: c.seoDesc,
    inLanguage: lang,
    step: c.steps.map((s, i) => ({ '@type': 'HowToStep', position: i + 1, text: s })),
  };

  return (
    <>
      <Seo
        title={c.title}
        description={c.seoDesc}
        jsonLd={[howToLd, breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: c.title, path: to('howto') }])]}
      />
      <PageHeader title={c.title} lead={c.lead} crumbs={[{ label: c.title }]} />
      <div className="container">
        <div className="row g-3 g-md-4">
          <div className="col-12 col-lg-6">
            <section className="panel prose h-100">
              <h2>{c.goalTitle}</h2>
              {c.goal.map((p) => <p key={p}>{p}</p>)}
            </section>
          </div>
          <div className="col-12 col-lg-6">
            <section className="panel prose h-100">
              <h2>{c.stepsTitle}</h2>
              <ol className="mb-0">{c.steps.map((s) => <li key={s} className="mb-2">{s}</li>)}</ol>
            </section>
          </div>
          <div className="col-12 col-lg-6">
            <section className="panel prose h-100">
              <h2>{c.winTitle}</h2>
              <ul className="mb-0">{c.win.map((s) => <li key={s} className="mb-2">{s}</li>)}</ul>
            </section>
          </div>
          <div className="col-12 col-lg-6">
            <section className="panel prose h-100">
              <h2>{c.nextTitle}</h2>
              <p>{c.next(to)}</p>
              <Link to={to('play')} className="btn btn-ink">{t('nav.playNow')}</Link>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
